import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSearch, faTimes, faTrash } from "@fortawesome/pro-regular-svg-icons";

import { StatusCode } from "api/protocols";

import {
  GetParticipant,
  DeleteLeagueParticipant,
  PostParticipant,
  PutParticipant,
  PutExportParticipants,
  ImportLeaguePlayers,
  CopyLeagueParticipants,
  GetLeagueRosterTemplate,
} from "api/rpc/2024-04/facilityAdmin/league/participant";

import { showError, showSuccess } from "redux/actions/ui";
import { ILeague, ILeagueParticipant } from "redux/reducers/models/league";

import { useAppDispatch } from "hooks/redux";
import { sortObj, valueToString } from "helpers/Helpers";

import Page from "components/page/Page";
import Card from "components/card/Card";
import { Badge } from "components/badge/Badge";
import Sheet from "components/sheet/Sheet";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Tabs from "components/tabs/Tabs";
import Search from "components/search/Search";
import { Select } from "components/select/index";
import Icon from "components/icon/Icon";

import "./LeaguePlayers.scss";
import { searchHistoryUpdateSearch } from "redux/actions/searchBars/searchHistory";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import Callout from "components/callout/Callout";
import { GetLeague } from "api/rpc/2024-04/facilityAdmin/league/league";
import { GetCustomer } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { ICustomer } from "redux/reducers/models/customer";
import DataTable from "components/dataTable/DataTable";
import Checkbox from "components/form/checkbox/Checkbox";
import { isEqual } from "lodash";
import ActionBar from "components/actionBar/ActionBar";
import useModal from "hooks/modals/useModal";
import {
  GetLeagueRegistrationInputResponse,
  GetLeagueRegistrationInputResponseExport,
  TLeagueRegistrationInputResponse,
} from "api/rpc/2024-04/facilityAdmin/league/registration/inputs";
import Spin from "components/spin/spin";
import axios, { CancelToken } from "axios";

interface IMainState {
  leaguePlayers: ILeagueParticipant[];
  selectedPlayers: number[];
  search: string;
  customers: ICustomer[];
  league: any;
  playerQuery: string;
}

interface INewPlayerState {
  first_name: string;
  last_name: string;
  email_address: string;
  phone_number: string;
  newPlayerSheetActive: boolean;
}

interface IImportState {
  modalVisible: boolean;
  selectedFile: File;
}

interface ICopyPlayerState {
  showCopyPlayers: boolean;
  selectedLeague: any;
  leagueSearch: string;
  leagues: Array<ILeague>;
  leagueSearching: boolean;
}

interface IPlayerAnswersModal {
  answers: TLeagueRegistrationInputResponse[];
  participantId: number;
  loadingStatus: "finished" | "loading" | "failed";
}

export default function LeaguePlayers() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { leagueId } = useParams<{ leagueId: string }>();
  const fileUploadRef = useRef(null);
  const { Option } = Select;

  const [state, setState] = useState<IMainState>({
    leaguePlayers: [],
    selectedPlayers: [],
    search: undefined,
    customers: [],
    league: null,
    playerQuery: "",
  });

  const visiblePlayers: ILeagueParticipant[] = useMemo(() => {
    if (state.leaguePlayers == null || state.leaguePlayers.length === 0) {
      return [];
    } else if (state.playerQuery !== "") {
      return state.leaguePlayers.filter(player =>
        player.customer.full_name.toLowerCase().includes(state.playerQuery.toLowerCase()),
      );
    } else {
      return state.leaguePlayers;
    }
  }, [state.leaguePlayers, state.playerQuery]);

  const {
    state: playerAnswersModal,
    closeModal: closePlayerAnswersModal,
    updateModal: updatePlayerAnswersModal,
  } = useModal<IPlayerAnswersModal>({
    answers: [],
    participantId: null,
    loadingStatus: "finished",
    clearOnClose: true,
  });

  const [newPlayerState, setNewPlayerState] = useState<INewPlayerState>({
    first_name: "",
    last_name: "",
    email_address: "",
    phone_number: "",
    newPlayerSheetActive: false,
  });

  const [importState, setImportState] = useState<IImportState>({
    modalVisible: false,
    selectedFile: null,
  });

  const [copyPlayerState, setCopyPlayerState] = useState<ICopyPlayerState>({
    showCopyPlayers: false,
    selectedLeague: null,
    leagueSearch: "",
    leagues: [],
    leagueSearching: false,
  });

  const [leagues] = useState<Array<ILeague>>([]);
  const [selected, setSelected] = useState(0); // Handle tab change

  useEffect(() => {
    void loadParticipants();
    void loadLeague();
  }, []);

  async function loadLeagueRegistrationInputResponse(cancelToken: CancelToken) {
    const playerResponsesRes = await GetLeagueRegistrationInputResponse(
      { participant_id: playerAnswersModal.participantId },
      false,
      cancelToken,
    );

    if (cancelToken?.reason) {
      updatePlayerAnswersModal({
        loadingStatus: "finished",
        answers: [],
      });

      return;
    } else if (playerResponsesRes.status !== StatusCode.OK) {
      updatePlayerAnswersModal({
        loadingStatus: "failed",
      });

      return;
    }

    updatePlayerAnswersModal({
      loadingStatus: "finished",
      answers: playerResponsesRes.data,
    });
  }

  useEffect(() => {
    const source = axios.CancelToken.source();

    if (playerAnswersModal.loadingStatus === "loading") {
      void loadLeagueRegistrationInputResponse(source.token);
    }

    return () => source.cancel();
  }, [playerAnswersModal.loadingStatus]);

  useEffect(() => {
    // debounced due to <Search /> callback
    if (state.search !== undefined) {
      void loadCustomers(state.search);
    }
  }, [state.search]);

  async function loadParticipants() {
    const params = {
      league_id: leagueId,
    };

    const leaguePlayersRes = await GetParticipant(params, true);

    if (leaguePlayersRes.status === StatusCode.OK) {
      setState(prevState => ({
        ...prevState,
        leaguePlayers: leaguePlayersRes.data,
      }));
    }
  }

  async function loadLeague() {
    const leagueRes = await GetLeague(
      {
        id: leagueId,
      },
      true,
    );

    if (leagueRes.status === StatusCode.OK) {
      setState(prevState => ({
        ...prevState,
        league: leagueRes.data[0],
      }));
    }
  }

  function createPlayerInputChange(e: any) {
    const id = e.target.id;
    const value = e.target.value;

    setNewPlayerState(prev => ({ ...prev, [id]: value }));
  }

  function closeNewPlayerSheet() {
    setNewPlayerState(prev => ({
      ...prev,
      newPlayerSheetActive: false,
      first_name: "",
      last_name: "",
      email_address: "",
      phone_number: "",
    }));

    setState(prevState => ({ ...prevState, search: "" }));
    dispatch(searchHistoryUpdateSearch("add_league_participant", ""));

    setSelected(0);
  }

  async function createPlayer() {
    const params = {
      league_id: Number(leagueId),
      first_name: newPlayerState.first_name,
      last_name: newPlayerState.last_name,
      email: newPlayerState.email_address,
      phone_number: newPlayerState.phone_number,
    };

    const addPlayerToLeagueRes = await PostParticipant(params, true);
    if (addPlayerToLeagueRes.status === StatusCode.OK) {
      void closeNewPlayerSheet();
      void loadParticipants();
    }
  }

  async function loadCustomers(searchQuery: string) {
    if (searchQuery !== undefined) {
      if (searchQuery !== "") {
        const customerRes = await GetCustomer({ search: searchQuery }, true);
        if (customerRes.status !== StatusCode.OK) {
          return;
        }

        setState(prev => ({ ...prev, customers: customerRes.data }));
      } else {
        setState(prev => ({ ...prev, customers: [] }));
      }
    }
  }

  async function updatePlayerFinancialStatus(event: React.MouseEvent<HTMLElement, MouseEvent>, player: any) {
    event.stopPropagation();

    const wantedStatus = player.order_financial_status == "paid" || null ? "unpaid" : "paid";

    const params = {
      id: player.id,
      order_financial_status: wantedStatus,
    };

    // Update player within the League
    const updatePlayerInLeagueRes = await PutParticipant(params, true);
    if (updatePlayerInLeagueRes.status !== StatusCode.OK) {
      return;
    }

    void loadParticipants(); // refresh the table
    void loadLeague();
  }

  async function deleteLeagueParticipants(participant_ids: number[]) {
    const deleteParticipantRes = await DeleteLeagueParticipant({ participant_ids }, true);

    if (deleteParticipantRes.status === StatusCode.OK) {
      dispatch(showSuccess(t("secure.facility.league.players.022")));
      void loadParticipants(); // refresh the table
      void loadLeague();
      setState(prev => ({ ...prev, selectedPlayers: [] }));
    } else {
      dispatch(showError(t("secure.facility.league.players.023")));
    }
  }

  async function deleteLeagueParticipant(participant_id: number) {
    const deleteParticipantRes = await DeleteLeagueParticipant({ participant_id }, true);

    if (deleteParticipantRes.status === StatusCode.OK) {
      dispatch(showSuccess(t("secure.facility.league.players.024")));
      void loadParticipants(); // refresh the table
      void loadLeague();
      setState(prev => ({
        ...prev,
        selectedPlayers: prev.selectedPlayers.filter(selectedPlayer => selectedPlayer !== participant_id),
      }));
    } else {
      dispatch(showError(t("secure.facility.league.players.025")));
    }
  }

  const tabs = [
    {
      id: "search-player",
      content: t("secure.facility.league.players.002"),
    },
    {
      id: "add-player",
      content: t("secure.facility.league.players.003"),
    },
  ];

  async function addNewPlayer(customer: any) {
    const params = {
      league_id: leagueId,
      customer_id: Number(customer.id),
    };

    console.log(params);

    const addPlayerToLeagueRes = await PostParticipant(params, true);
    if (addPlayerToLeagueRes.status === StatusCode.OK) {
      closeNewPlayerSheet();
      void loadParticipants();
      void loadLeague();
    }
  }

  async function exportLeaguePlayers() {
    const exportRes = await PutExportParticipants({ league_id: leagueId, type: "csv" }, true);

    if (exportRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.league.players.026")));
      return;
    }

    window.open(exportRes?.data as string, "_blank").focus();
  }

  async function exportLeagueResponses() {
    const exportRes = await GetLeagueRegistrationInputResponseExport({ league_id: Number(leagueId) }, true);

    if (exportRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.league.players.027")));
      return;
    }

    const templateExport = document.createElement("a");

    templateExport.href = "data:text/csv;charset=utf-8," + encodeURI(exportRes.data);
    templateExport.target = "_blank";
    templateExport.download = `${"Player_Responses"}_${moment(new Date()).format("YYYY-MM-DD").toString()}`
      .replace(/ /g, "_")
      .toLowerCase()
      .concat(".csv");
    templateExport.click();
  }

  const primaryAction = {
    content: (
      <div>
        <FontAwesomeIcon className="mr-2" icon={faPlus} /> {t("secure.facility.league.players.001")}
      </div>
    ),
    action: () => setNewPlayerState(prev => ({ ...prev, newPlayerSheetActive: true })),
  };

  function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files;
    if (file && file.length > 0) {
      setImportState(prevState => ({ ...prevState, selectedFile: file[0] }));
    }
  }

  async function importLeaguePlayers() {
    const formData = new FormData();

    formData.append("file", importState.selectedFile);
    formData.append("league_id", leagueId);

    const importRes = await ImportLeaguePlayers(formData, true);

    if (importRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error importing league players")));
      return;
    }

    dispatch(showSuccess(t("League Players succesfully imported")));

    fileUploadRef.current.value = "";
    setImportState(prevState => ({ ...prevState, selectedFile: null, modalVisible: false }));
    void loadParticipants();
  }

  async function copyPlayer() {
    const params = {
      to_league_id: copyPlayerState.selectedLeague.id,
      from_league_id: leagueId,
    };

    const copyRes = await CopyLeagueParticipants(params, true);

    if (copyRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error copying players")));
      return;
    }

    dispatch(showSuccess(t("Successfully copied players")));

    setCopyPlayerState(prevState => ({ ...prevState, showCopyPlayers: false, selectedLeague: null }));
  }

  function displayLeagues() {
    if (copyPlayerState?.leagues && copyPlayerState?.leagues?.length > 0 && copyPlayerState?.leagueSearch !== "") {
      return copyPlayerState?.leagues;
    } else {
      return leagues;
    }
  }

  async function loadSearchedLeagues(search: string) {
    const params = {
      search: search,
    };

    const leagueRes = await GetLeague(params, false);

    if (leagueRes.status !== StatusCode.OK) {
      dispatch(showError(leagueRes.message));

      return [];
    }

    return leagueRes.data;
  }

  function handleLeagueChange(value: any, id: string, league: ILeague) {
    setCopyPlayerState(prevState => ({ ...prevState, leagueSearch: "", selectedLeague: league }));
  }

  function removeSelectedLeague() {
    setCopyPlayerState(prevState => ({
      ...prevState,
      selectedLeague: null,
      leagues: [],
      leagueSearching: false,
    }));
  }

  //Load Leagues
  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout = null;
    const search = () => {
      timeoutId = global.setTimeout(() => {
        void (async () => {
          if (copyPlayerState.leagueSearch !== "") {
            try {
              setCopyPlayerState(prevState => ({ ...prevState, leagueSearching: true }));
              const leagues = await loadSearchedLeagues(copyPlayerState.leagueSearch);
              if (mounted) {
                setCopyPlayerState(prevState => ({ ...prevState, leagues: leagues, leagueSearching: false }));
              }
            } catch (error) {
              console.log("err", error);
            }
            return;
          } else {
            setCopyPlayerState(prevState => ({ ...prevState, leagues: [], leagueSearching: false }));
          }
        })();
      }, 1000);
    };
    search();
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [copyPlayerState.leagueSearch]);

  function handleViewOrderClick(orderId: number) {
    window.open("/admin/order/" + String(orderId));
  }

  function handleViewAnswersClick(participantId: number) {
    updatePlayerAnswersModal({
      isOpen: true,
      participantId,
      loadingStatus: "loading",
    });
  }

  function handleSelectAllPlayers() {
    const allVisiblePlayers = visiblePlayers.map(visiblePlayer => visiblePlayer.id);

    if (isEqual(allVisiblePlayers, state.selectedPlayers)) {
      setState(prev => {
        return {
          ...prev,
          selectedPlayers: [],
        };
      });
    } else {
      setState(prev => {
        return {
          ...prev,
          selectedPlayers: allVisiblePlayers,
        };
      });
    }
  }

  function selectPlayer(playerId: number) {
    setState(prev => {
      const updatedSelectedPlayers = [...prev.selectedPlayers];

      const matchingSelectedPlayerIndex = updatedSelectedPlayers.findIndex(
        selectedPlayer => selectedPlayer === playerId,
      );

      if (matchingSelectedPlayerIndex === -1) {
        updatedSelectedPlayers.push(playerId);
      } else {
        updatedSelectedPlayers.splice(matchingSelectedPlayerIndex, 1);
      }

      return {
        ...prev,
        selectedPlayers: updatedSelectedPlayers,
      };
    });
  }

  function renderPlayerAnswersModalContent() {
    if (playerAnswersModal.loadingStatus === "loading") {
      return (
        <div className="league-players-player-form-spinner-container">
          <Spin className="league-players-player-form-spinner" />
        </div>
      );
    } else if (playerAnswersModal.loadingStatus === "failed") {
      return <Callout type="error" title={t("secure.facility.league.players.028")} />;
    } else if (playerAnswersModal.loadingStatus === "finished") {
      if (playerAnswersModal.answers.length === 0) {
        return <Callout type="info" title={t("secure.facility.league.players.029")} />;
      } else {
        return (
          <div className="league-players-player-form-responses">
            {playerAnswersModal.answers.map((answer, index) => {
              return (
                <React.Fragment key={index}>
                  {answer.type === "checkbox" && (
                    <Checkbox readOnly size="small" checked={answer.value as boolean} label={answer.label} />
                  )}
                  {answer.type === "input" && <Input readOnly value={answer.value} label={answer.label} />}
                  {answer.type === "select" && (
                    <Select defaultValue={answer.value as string} label={answer.label}>
                      <Option value={answer.value as string}>{answer.value}</Option>
                    </Select>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        );
      }
    }
  }

  async function downloadRosterTemplate() {
    const downloadRes = await GetLeagueRosterTemplate(
      {
        league_id: Number(leagueId),
      },
      true,
    );

    if (downloadRes.status !== StatusCode.OK) {
      dispatch(showError("Error retrieving template"));
      return;
    }

    window.open(downloadRes.data as string);
  }

  return (
    <Page
      title={t("secure.facility.league.players.004")}
      primaryAction={primaryAction}
      // secondaryActions={[secondaryAction]}
      multipleActionDropdownAction={{
        label: t("Options"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              icon: "plus",
              type: "handler",
              label: t("secure.facility.league.players.001"),
              handler: () => setNewPlayerState(prev => ({ ...prev, newPlayerSheetActive: true })),
            },
            {
              icon: "circle-arrow-down-right",
              type: "handler",
              label: t("Import"),
              handler: () => setImportState(prevState => ({ ...prevState, modalVisible: true })),
            },
            {
              icon: "arrow-right-from-bracket",
              type: "handler",
              label: t("Export"),
              handler: exportLeaguePlayers,
            },
            {
              icon: "arrow-down-to-line",
              type: "handler",
              label: t("Download Responses"),
              handler: exportLeagueResponses,
            },
            {
              icon: "copy",
              type: "handler",
              label: t("Copy Players"),
              handler: () => setCopyPlayerState(prevState => ({ ...prevState, showCopyPlayers: true })),
            },
            {
              icon: "arrow-down-to-line",
              type: "handler",
              label: t("Download Import Template"),
              handler: downloadRosterTemplate,
            },
          ],
        },
      }}
    >
      <Search
        className="league-players-search"
        leftIcon={<FontAwesomeIcon icon={faSearch} />}
        placeholder={t("secure.facility.league.players.030")}
        searchCallback={searchValue => setState(prevState => ({ ...prevState, playerQuery: searchValue }))}
        historyKey="league-players-player-query"
      />

      <ActionBar
        visible={state.selectedPlayers.length > 0}
        selectedItemIds={state.selectedPlayers}
        clearSelectedItemIds={() => setState(prev => ({ ...prev, selectedPlayers: [] }))}
        itemSingularTitle={t("secure.facility.league.players.031")}
        itemPluralTitle={t("secure.facility.league.players.032")}
        primaryActions={[
          {
            icon: faTrash,
            title: t("secure.facility.league.players.033"),
            action: deleteLeagueParticipants,
          },
        ]}
      >
        <Card
          style={{ marginBottom: "40px" }}
          title={t("secure.facility.league.players.034")}
          titleDetails={[
            {
              content: (
                <Badge type="primary" className="mr-2">
                  {t("secure.facility.league.players.018")} {state.leaguePlayers?.length}
                </Badge>
              ),
            },
            {
              content: (
                <Badge>
                  {t("secure.facility.league.players.019")} {state.league?.total_paid_participant_count}
                </Badge>
              ),
            },
          ]}
        >
          <Card.Section>
            <DataTable
              columns={[
                {
                  label: "",
                  content: (
                    <Checkbox
                      size="small"
                      checked={
                        visiblePlayers.length > 0 &&
                        visiblePlayers.every(visiblePlayer => state.selectedPlayers.includes(visiblePlayer.id))
                      }
                      onChange={handleSelectAllPlayers}
                    />
                  ),
                },
                { label: t("secure.facility.league.players.005"), width: "100%" },
                { label: t("secure.facility.league.players.006") },
                { label: t("secure.facility.league.players.007") },
                { label: t("secure.facility.league.players.008") },
                { id: "created_at", label: t("secure.facility.league.players.009") },
                { label: "" },
              ]}
              overflow
              sort={(key, direction) => {
                if (state.leaguePlayers) {
                  setState(prev => {
                    return {
                      ...prev,
                      leaguePlayers: sortObj(prev.leaguePlayers, key, direction),
                    };
                  });
                }
              }}
            >
              {visiblePlayers.map(player => {
                return (
                  <tr key={player.id}>
                    <td>
                      <Checkbox
                        size="small"
                        checked={state.selectedPlayers.includes(player.id)}
                        onChange={() => selectPlayer(player.id)}
                      />
                    </td>
                    <td>
                      <div>{player.customer.full_name}</div>
                      <div className="league-players-customer-type">{player.customer.customer_type}</div>
                    </td>
                    <td>{player.customer.email}</td>
                    <td>{player.customer.phone}</td>

                    <td>
                      {player.order_financial_status ? (
                        <>
                          {player.order_financial_status === "paid" ? (
                            <span style={{ cursor: "pointer" }}>
                              <Badge
                                type="success"
                                size="medium"
                                iconLeft
                                icon="check"
                                onClick={e => updatePlayerFinancialStatus(e, player)}
                              >
                                {valueToString(player.order_financial_status, "capitalize")}
                              </Badge>
                            </span>
                          ) : (
                            <span style={{ cursor: "pointer" }}>
                              <Badge
                                type="gray"
                                size="medium"
                                iconLeft
                                icon="circle"
                                onClick={e => updatePlayerFinancialStatus(e, player)}
                              >
                                {valueToString(player.order_financial_status, "capitalize")}
                              </Badge>
                            </span>
                          )}
                        </>
                      ) : (
                        <span style={{ cursor: "pointer" }}>
                          <Badge
                            type="gray"
                            size="medium"
                            iconLeft
                            icon="circle"
                            onClick={e => updatePlayerFinancialStatus(e, player)}
                          >
                            {t("secure.facility.league.players.020")}
                          </Badge>
                        </span>
                      )}
                    </td>

                    <td>
                      <span>{moment.utc(player.created_at).local().format("MMM DD, YYYY")}</span>
                    </td>
                    <td>
                      <NavigationDropdownNew
                        showPlainTextLabel
                        rightAlign
                        label={<FontAwesomeIcon icon={["far", "chevron-down"]} />}
                        sections={[
                          [
                            {
                              icon: "ban",
                              label: t("secure.facility.league.players.035"),
                              onClick: () => deleteLeagueParticipant(player.id),
                            },
                            {
                              label: t("secure.facility.league.players.036"),
                              icon: "magnifying-glass",
                              onClick: () => handleViewOrderClick(player.order_id),
                              disabled: player.order_id === null,
                            },
                            {
                              label: t("secure.facility.league.players.037"),
                              icon: "eye",
                              onClick: () => handleViewAnswersClick(player.id),
                              disabled: false,
                            },
                          ],
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </DataTable>
          </Card.Section>
        </Card>
      </ActionBar>

      <Sheet
        open={playerAnswersModal.isOpen}
        size="medium"
        closable
        stacked
        title={t("secure.facility.league.players.038")}
        onCancel={closePlayerAnswersModal}
      >
        {renderPlayerAnswersModalContent()}
      </Sheet>

      <Sheet
        open={newPlayerState.newPlayerSheetActive}
        size="small"
        closable
        stacked
        title={t("secure.facility.league.players.010")}
        onCancel={closeNewPlayerSheet}
        onOk={createPlayer}
        cancelText={t("secure.facility.league.players.011")}
        okText={t("secure.facility.league.players.012")}
        okDisabled={
          newPlayerState.first_name === "" ||
          newPlayerState.last_name === "" ||
          (newPlayerState.email_address === "" && newPlayerState.phone_number === "")
        }
      >
        <Tabs tabs={tabs} selected={selected} onSelect={selectedTabIndex => setSelected(selectedTabIndex)}>
          {selected === 0 ? (
            <>
              <div className="flex-grow mb-4 mt-2">
                <Search
                  historyKey={"add_league_participant"}
                  searchCallback={searchValue => setState(prev => ({ ...prev, search: searchValue }))}
                  placeholder={t("secure.facility.league.players.013")}
                />
              </div>

              <div className="flex-grow mb-4">
                <div>
                  {state.customers?.map((customer, index: number) => {
                    return (
                      <div
                        key={index}
                        className="flex justify-between league-customer-results"
                        onClick={() => addNewPlayer(customer)}
                      >
                        <div>
                          <div className="text-lg text-semibold">{customer?.full_name}</div>
                          <div className="text-sm text-gray-500">{customer.email}</div>
                          <div className="text-sm text-gray-500">{customer.phone ? customer.phone : null}</div>
                          <div className="text-sm text-gray-500">
                            {customer.customer_type ? customer.customer_type : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          ) : null}

          {selected === 1 ? (
            <FormLayout>
              <FormLayout.Group>
                <Input
                  id="first_name"
                  label={t("secure.facility.league.players.014")}
                  value={newPlayerState.first_name}
                  onChange={createPlayerInputChange}
                />
                <Input
                  id="last_name"
                  label={t("secure.facility.league.players.015")}
                  value={newPlayerState.last_name}
                  onChange={createPlayerInputChange}
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <Input
                  id="email_address"
                  label={t("secure.facility.league.players.016")}
                  value={newPlayerState.email_address}
                  onChange={createPlayerInputChange}
                />
                <Input
                  label={t("secure.facility.league.players.017")}
                  id="phone_number"
                  value={newPlayerState.phone_number}
                  onChange={createPlayerInputChange}
                />
              </FormLayout.Group>
            </FormLayout>
          ) : null}
        </Tabs>
      </Sheet>

      <Sheet
        title={t("Select File")}
        open={importState.modalVisible}
        size="small"
        closable
        onCancel={() => setImportState(prevState => ({ ...prevState, modalVisible: false }))}
        onOk={importLeaguePlayers}
        okText={t("Import")}
        okDisabled={!importState.selectedFile ? true : false}
      >
        <Callout
          type="info"
          title={!importState.selectedFile ? "No file selected" : "Selected File"}
          content={!importState.selectedFile ? t("Select a file to continue") : importState.selectedFile.name}
        />

        <div className="customer-import-container mt-2">
          <input
            ref={fileUploadRef}
            className="customer-import-input"
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
          />
          <FontAwesomeIcon className="customer-import-icon" icon={["far", "upload"]} />
        </div>
      </Sheet>

      <Sheet
        open={copyPlayerState.showCopyPlayers}
        size="small"
        closable
        stacked
        title={t("Select League")}
        onCancel={() => setCopyPlayerState(prevState => ({ ...prevState, showCopyPlayers: false, league: null }))}
        onOk={copyPlayer}
        cancelText={t("Cancel")}
        okText={t("Copy")}
        okDisabled={false}
      >
        <div style={{ height: "250px" }}>
          {copyPlayerState.selectedLeague ? (
            <div>
              <div>
                <p className="event-label">{t("Leagues")}</p>
              </div>

              <div className="selected-container">
                <div className="event-name">
                  <div>{copyPlayerState.selectedLeague.name}</div>

                  <div>
                    <button className="" onClick={removeSelectedLeague}>
                      <Icon icon={faTimes} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <Select
                showSearch
                label={t("Leagues")}
                onSearch={(query: string) => setCopyPlayerState(prevState => ({ ...prevState, leagueSearch: query }))}
                onChange={(value: any, league: ILeague) => handleLeagueChange(value, "selectedleague", league)}
                placeholder={t("Search leagues...")}
                allowClear
                searchValue={copyPlayerState.leagueSearch}
                showDropDownOnFocus={true}
                searching={copyPlayerState.leagueSearching}
              >
                {displayLeagues()?.map((league: any, index: number) => {
                  return (
                    <Option value={league?.id} key={index} extraValues={league}>
                      <div className="text-semibold text-lg">{league.name}</div>
                    </Option>
                  );
                })}
              </Select>
            </div>
          )}
        </div>
      </Sheet>
    </Page>
  );
}
